import type { FC } from 'react'
import styled from '@emotion/styled'

import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import { CompliantIcon, DataIcon, ToolIcon } from '@segment/matcha/components/Icons/Brandbook'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'
import IconWrapper from '@segment/matcha/components/IconWrapper'

import CardBase, * as Card from '@segment/matcha/recipes/Card'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

const copy = {
  heading: `Resources`,
}

const cards = [
  {
    title: `Automate privacy compliance`,
    description: `Set rules to proactively block, hash, and route sensitive data to accelerate compliance with GDPR, CCPA, and more.`,
    icon: CompliantIcon,
    link: `/product/privacy-portal/`,
  },
  {
    title: `Standardize your data`,
    description: `Align data collection throughout your organization by creating a single tracking plan across all your web and mobile apps.`,
    icon: DataIcon,
    link: `/product/protocols/`,
  },
  {
    title: `Collect and send data anywhere`,
    description: `Enable ${process.env.INTEGRATION_COUNT}+ of the best product analytics, A/B testing, and data warehouse tools or write your own custom integrations.`,
    icon: ToolIcon,
    link: `/catalog/`,
  },
]

const Resources: FC = () => {
  const pageConfig = usePageConfig()
  return (
    <Wrapper>
      <StyledSection variant="centered">
        <Section.Copy>
          <CopyTextBase centered={true}>
            <CopyText.Heading tag="h2" variant="h2">
              {copy.heading}
            </CopyText.Heading>
          </CopyTextBase>
        </Section.Copy>
        <Section.Contents>
          <Contents>
            <ColumnTrack overrides={{ md: 3 }}>
              {cards.map(({ link, title, description, icon: Icon }, idx) => (
                <CardBase key={idx} href={getAbsoluteUrl(link, pageConfig)}>
                  <Card.Body>
                    <Card.Accessories>
                      <Card.AccessoriesRow>
                        <IconWrapper variant="transparent">
                          <Icon size="large" />
                        </IconWrapper>
                      </Card.AccessoriesRow>
                    </Card.Accessories>
                    <Card.Copy>
                      <Card.Headline tag="h3">{title}</Card.Headline>
                      <Card.Description variant="pLarge">{description}</Card.Description>
                    </Card.Copy>
                    <Card.Action alignment="right">
                      <ChevronRightIcon />
                    </Card.Action>
                  </Card.Body>
                </CardBase>
              ))}
            </ColumnTrack>
          </Contents>
        </Section.Contents>
      </StyledSection>
    </Wrapper>
  )
}

export default Resources

const Wrapper = styled.div`
  background: ${({ theme }) => theme.palette.twilio.gray[10]};
`

const StyledSection = styled(SectionBase)`
  max-width: 1280px;

  ${Section.Wrapper} {
    row-gap: 40px;

    ${({ theme }) => theme.breakpoints.up(`lg`)} {
      row-gap: 64px;
    }
  }
`

const Contents = styled.div`
  position: relative;
  height: 100%;
`
